import { API_CALL, API_CALL_FAILURE, API_CALL_SUCCESS, SET_ACCOMMODATION_V2_SESSIONS } from 'actions/actionConstants'
import { CHECKOUT_ACCOMMODATION_V2_ENQUIRY } from 'actions/apiActionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { checkoutEnquiry, createEnquirySession } from 'api/accommodationBooking'
import { removeItems, updateCheckoutItem } from 'actions/CheckoutActions'
import { buildEnquirySessionPayload } from 'lib/accommodation/buildEnquirySessionPayload'
import {
  applyAccommodationV2ReplacementSession,
  buildReplacementEnquirySessionPayload,
} from 'lib/accommodation/buildReplacementEnquirySessionPayload'
import { hasAccommodationV2SessionError, isAccommodationV2PurchaseBlocked } from 'checkout/lib/utils/accommodationV2/outcomes'
import { isAccommodationV2Item } from 'lib/checkout/checkoutUtils'
import type { AppDispatch } from 'src/client/store'

export function setAccommodationV2Sessions(
  sessions: Array<App.AccommodationEnquirySession>,
): AppAction {
  return {
    type: SET_ACCOMMODATION_V2_SESSIONS,
    data: sessions,
  }
}

export interface InitAccommodationV2SessionSelection {
  rate: App.EnrichedPropertyRate
  occupancy: App.Occupants
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId' | 'tickets'>>
}

export interface InitAccommodationV2SessionsParams {
  /** One selection per cart item — rates may differ across multi-room individual mode. */
  selections: Array<InitAccommodationV2SessionSelection>
  checkIn: string
  checkOut: string
  region: string
  /** Shopped LuxPlus+ eligibility stamped onto each session for checkout enquiry. */
  isLuxPlusUser?: boolean
}

/**
 * Creates one enquiry session per selection in parallel.
 *
 * Fail-closed for booking: any create failure rejects the whole attempt so callers
 * abort to sold-out rather than proceeding with a partial set of session ids.
 * Successful creates from a partial failure are still dispatched (so Redux tracks
 * them); the FE has no delete API yet, so those orphans expire via server TTL.
 * Returns session ids in selection order.
 */
export async function initAccommodationV2Sessions(
  dispatch: AppDispatch,
  params: InitAccommodationV2SessionsParams,
): Promise<Array<string>> {
  const settled = await Promise.allSettled(params.selections.map(({ rate, occupancy, selectedOptionalBundles }) =>
    createEnquirySession(buildEnquirySessionPayload({
      rate,
      occupancy,
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      region: params.region,
      selectedOptionalBundles,
      ...(params.isLuxPlusUser && { isLuxPlusUser: true }),
    })),
  ))

  const createdSessions = settled.flatMap(result => (
    result.status === 'fulfilled' ? [result.value] : []
  ))
  if (createdSessions.length > 0) {
    dispatch(setAccommodationV2Sessions(createdSessions))
  }

  const firstRejection = settled.find(result => result.status === 'rejected')
  if (firstRejection?.status === 'rejected') {
    throw firstRejection.reason
  }

  return createdSessions.map(session => session.id)
}

export function refreshAccommodationV2Sessions(sessionIds: Array<string>): AppAction {
  return (dispatch, getState) => {
    if (sessionIds.length === 0) return

    dispatch({
      type: API_CALL,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      request: () => checkoutEnquiry({
        items: sessionIds.map(sessionId => ({ sessionId })),
        transactionKey: getState().checkout.cart.transactionKey,
      }),
    })
  }
}
/**
 * Accepts a mixed checkout-enquiry result: mint replacement sessions for `price_changed`
 * rooms, drop `unavailable` rooms, then re-enquire the survivors.
 */
export function acceptAccommodationV2EnquiryOutcomes(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const sessions = state.checkout.accommodationSessions
    const items = state.checkout.cart.items.filter(isAccommodationV2Item)

    const priceChangedItems = items.filter(item => (
      item.sessionId && sessions[item.sessionId]?.outcome === 'price_changed'
    ))
    const unavailableItemIds = new Set(
      items
        .filter(item => item.sessionId && sessions[item.sessionId]?.outcome === 'unavailable')
        .map(item => item.itemId),
    )
    const replacementSessionIds = new Map<string, string>()

    for (const item of priceChangedItems) {
      const record = item.sessionId ? sessions[item.sessionId] : undefined
      if (!record?.data || !record.rate) {
        throw new Error('Cannot accept a price change without a live enquiry rate')
      }

      const session = await createEnquirySession(
        buildReplacementEnquirySessionPayload(record.data, record.rate),
      )
      replacementSessionIds.set(item.itemId, session.id)
      dispatch(setAccommodationV2Sessions([session]))
      dispatch(updateCheckoutItem(applyAccommodationV2ReplacementSession(item, record.rate, session.id)))
    }

    if (unavailableItemIds.size > 0) {
      dispatch(removeItems([...unavailableItemIds]))
    }

    const remainingSessionIds = items.flatMap((item) => {
      if (unavailableItemIds.has(item.itemId)) return []
      const replacementId = replacementSessionIds.get(item.itemId)
      if (replacementId) return [replacementId]
      return item.sessionId ? [item.sessionId] : []
    })

    if (remainingSessionIds.length > 0) {
      try {
        const data = await checkoutEnquiry({
          items: remainingSessionIds.map(sessionId => ({ sessionId })),
          transactionKey: getState().checkout.cart.transactionKey,
        })
        dispatch({
          type: API_CALL_SUCCESS,
          api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
          requestedSessionIds: remainingSessionIds,
          data,
        })
      } catch(error) {
        dispatch({
          type: API_CALL_FAILURE,
          api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
          requestedSessionIds: remainingSessionIds,
          error,
        })
      }
    }

    const nextState = getState()
    if (hasAccommodationV2SessionError(nextState.checkout.cart.items, nextState.checkout.accommodationSessions)) {
      throw new Error('Accommodation checkout enquiry refresh failed')
    }

    return {
      stillBlocked: isAccommodationV2PurchaseBlocked(
        nextState.checkout.cart.items,
        nextState.checkout.accommodationSessions,
      ),
    }
  }
}
